import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { siteBaseUrl, isGestosAgeisDomain } from '@/lib/siteDomain';
import { getSiteImage, SITE_IMAGE_KEYS } from '@/lib/siteImages';

// Gestos Ágeis domains (.pt / .com / .store) each keep a self-canonical so they rank
// independently; non-Gestos domains (orthocare.*) that reach /gestosageis canonicalize
// to gestosageis.pt — the canonical source of this Gestos Ágeis rental content.
const BASE = isGestosAgeisDomain() ? siteBaseUrl() : 'https://gestosageis.pt';
const LOGO = 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/93a4b2fb8_1777227440051110206008173168802.png';
const HERO = 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/05c0ac79d_Screenshot_20260425_214044_Google.jpg';

const faqPt = [
  { q: 'Onde alugar equipamento ortopédico online em Portugal?', a: 'No site da Gestos Ágeis (gestosageis.pt) aluga cadeiras de rodas, camas articuladas, andarilhos e scooters de mobilidade com entrega ao domicílio em todo Portugal, normalmente em 24 horas. Escolha o equipamento, as datas e o local de entrega, assine o contrato digital e pague online — sem deslocações a loja.' },
  { q: 'A Gestos Ágeis vende ou aluga material ortopédico?', a: 'A Gestos Ágeis faz venda e aluguer de material ortopédico em Lisboa e para todo Portugal. Pode comprar cadeiras de rodas, andarilhos, bengalas, camas articuladas, scooters de mobilidade e ajudas técnicas, ou optar pelo aluguer curto ou longo prazo com entrega e recolha incluídas.' },
  { q: 'Qual é o prazo de entrega do equipamento ortopédico?', a: 'Normalmente entregamos no dia seguinte à confirmação da reserva. Para pedidos urgentes na área de Lisboa conseguimos entregar no próprio dia. A entrega e montagem são feitas por técnicos especializados.' },
  { q: 'Posso cancelar a minha reserva?', a: 'Sim. Cancelamentos com mais de 48h de antecedência são gratuitos. Para cancelamentos com menos de 48h poderá ser aplicada uma taxa.' },
  { q: 'Os equipamentos são novos?', a: 'Os nossos equipamentos são regularmente revistos, higienizados e certificados. São novos ou recondicionados em excelente estado de conservação e funcionamento.' },
  { q: 'Quais são os métodos de pagamento aceites?', a: 'Aceitamos MB Way, Multibanco, transferência bancária e pagamento em loja (Estrada de Benfica 739A, Lisboa).' },
  { q: 'Existe caução no aluguer?', a: 'Alguns equipamentos requerem caução reembolsável, devolvida na íntegra na recolha se o equipamento estiver em bom estado.' },
  { q: 'A Gestos Ágeis tem loja física?', a: 'Sim. A Gestos Ágeis Ortopedia tem loja na Estrada de Benfica 739A, Lisboa (Benfica), com horário de 2ª a 6ª das 9h30 às 19h. NIF: 515948039.' },
  { q: 'Fazem entregas fora de Lisboa?', a: 'Sim. Entregamos em todo Portugal Continental — Porto, Braga, Coimbra, Aveiro, Cascais, Setúbal, Faro, Évora e demais concelhos. Recolha também em loja.' }
];

const faqEn = [
  { q: 'Where can I rent orthopedic equipment online in Portugal?', a: 'On the Gestos Ágeis website (gestosageis.pt) you can rent wheelchairs, hospital beds, walkers and mobility scooters with home delivery across Portugal, usually within 24 hours. Choose the equipment, dates and delivery location, sign the digital contract and pay online — no store visit required.' },
  { q: 'Does Gestos Ágeis sell or rent orthopedic equipment?', a: 'Gestos Ágeis sells and rents orthopedic equipment in Lisbon and across Portugal. You can buy wheelchairs, walkers, canes, hospital beds, mobility scooters and daily living aids, or rent short or long term with delivery and pickup included.' },
  { q: 'What is the delivery time for orthopedic equipment?', a: 'We usually deliver the day after the reservation is confirmed. For urgent requests in the Lisbon area we can deliver the same day. Delivery and assembly are carried out by specialized technicians.' },
  { q: 'Can I cancel my reservation?', a: 'Yes. Cancellations made more than 48 hours in advance are free. Cancellations within 48 hours may incur a fee.' },
  { q: 'Is the equipment new?', a: 'Our equipment is regularly inspected, sanitized and certified. It is new or expertly reconditioned in excellent working condition.' },
  { q: 'What payment methods are accepted?', a: 'We accept MB Way, Multibanco, bank transfer and in-store payment (Estrada de Benfica 739A, Lisbon).' },
  { q: 'Is there a security deposit?', a: 'Some equipment requires a refundable deposit, returned in full upon collection if the equipment is in good condition.' },
  { q: 'Does Gestos Ágeis have a physical store?', a: 'Yes. Gestos Ágeis Ortopedia has a store at Estrada de Benfica 739A, Lisbon (Benfica), open Monday to Friday 9:30am–7pm. Tax ID (NIF): 515948039.' }
];

const setMeta = (sel, attr, val) => {
  let el = document.head.querySelector(sel);
  if (!el) {
    if (attr === 'href') {
      el = document.createElement('link');
      el.setAttribute('rel', sel.match(/\[rel="([^"]+)"\]/)?.[1] || 'canonical');
    } else {
      el = document.createElement('meta');
      // Extract name="..." or property="..." from the selector so newly
      // created meta tags are valid (previously only content was set, leaving
      // nameless meta tags that Google ignores).
      const nameMatch = sel.match(/\[name="([^"]+)"\]/);
      const propMatch = sel.match(/\[property="([^"]+)"\]/);
      if (nameMatch) el.setAttribute('name', nameMatch[1]);
      if (propMatch) el.setAttribute('property', propMatch[1]);
    }
    document.head.appendChild(el);
  }
  if (attr === 'content') el.setAttribute('content', val);
  if (attr === 'href') el.setAttribute('href', val);
};

const setTitle = (t) => { document.title = t; };

const setJsonLd = (id, obj) => {
  let s = document.getElementById(id);
  if (s) s.remove();
  s = document.createElement('script');
  s.type = 'application/ld+json';
  s.id = id;
  s.textContent = JSON.stringify(obj);
  document.head.appendChild(s);
};

export default function GestosAgeisSEO({ language = 'pt' }) {
  const { data: settings = [] } = useQuery({
    queryKey: ['settings', 'v4'],
    queryFn: () => base44.entities.Settings.list('-created_date', 1000),
    staleTime: 1000 * 60 * 5,
  });
  const ogImg = getSiteImage(settings, SITE_IMAGE_KEYS.ogGestos);
  useEffect(() => {
    const pt = language !== 'en';
    const isPt = pt;

    setTitle(isPt
      ? 'Aluguer de Material Ortopédico em Lisboa | Gestos Ágeis'
      : 'Gestos Ágeis | Orthopedic Equipment Sales & Rental in Portugal – Wheelchair, Hospital Bed, Walker, Scooter');

    setMeta('meta[name="description"]', 'content', isPt
      ? 'Aluguer e venda de equipamento ortopédico em Lisboa: cadeiras de rodas, camas articuladas, andarilhos, canadianas e scooters. Entrega ao domicílio em 24h. Apoio via WhatsApp.'
      : 'Gestos Ágeis – Sales and rental of orthopedic equipment in Lisbon and across Portugal. Wheelchairs, hospital beds, walkers, mobility scooters and daily living aids. 24h home delivery, store in Benfica. VAT: PT515948039.');

    setMeta('meta[name="keywords"]', 'content', isPt
      ? 'gestos ageis, ortopedia lisboa, aluguer cadeira de rodas, aluguer cama articulada, aluguer andarilho, aluguer scooter mobilidade, venda material ortopédico, aluguer material ortopédico portugal, loja ortopedia benfica, ajudas técnicas, equipamento ortopédico, entrega domicílio 24h, cadeira de rodas lisboa, cadeira de rodas porto, aluguer poltrona, NIF 515948039'
      : 'gestos ageis, orthopedic store lisbon, wheelchair rental portugal, hospital bed rental, walker rental, mobility scooter rental, orthopedic equipment sales portugal, orthopedic store benfica, daily living aids, home delivery 24h, wheelchair lisbon, VAT PT515948039');

    // On non-Gestos domains (e.g. orthocare.pt/gestosageis) this page
    // canonicalizes cross-domain to gestosageis.pt.  Mark it noindex so
    // Google does not index the orthocare.pt copy and then consolidate
    // other orthocare.pt pages (including the homepage) with gestosageis
    // pages — the root cause of the GSC "Página duplicada" canonical
    // conflict on https://orthocare.pt/.
    const isGestos = isGestosAgeisDomain();
    setMeta('meta[name="robots"]', 'content', isGestos
      ? 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
      : 'noindex, nofollow');
    setMeta('meta[name="author"]', 'content', 'Gestos Ágeis Unipessoal Lda – NIF: 515948039');
    setMeta('meta[name="geo.region"]', 'content', 'PT-11');
    setMeta('meta[name="geo.placename"]', 'content', 'Lisboa, Portugal');
    setMeta('meta[name="geo.position"]', 'content', '38.7436;-9.1952');
    setMeta('meta[name="ICBM"]', 'content', '38.7436, -9.1952');

    setMeta('link[rel="canonical"]', 'href', `${BASE}/`);
    setMeta('link[rel="alternate"][hreflang="pt-PT"]', 'href', `${BASE}/`);
    setMeta('link[rel="alternate"][hreflang="pt"]', 'href', `${BASE}/`);
    setMeta('link[rel="alternate"][hreflang="en"]', 'href', `${BASE}/?lang=en`);
    setMeta('link[rel="alternate"][hreflang="en-GB"]', 'href', `${BASE}/?lang=en`);
    setMeta('link[rel="alternate"][hreflang="en-US"]', 'href', `${BASE}/?lang=en`);
    setMeta('link[rel="alternate"][hreflang="es"]', 'href', `${BASE}/?lang=es`);
    setMeta('link[rel="alternate"][hreflang="es-ES"]', 'href', `${BASE}/?lang=es`);
    setMeta('link[rel="alternate"][hreflang="fr"]', 'href', `${BASE}/?lang=fr`);
    setMeta('link[rel="alternate"][hreflang="fr-FR"]', 'href', `${BASE}/?lang=fr`);
    setMeta('link[rel="alternate"][hreflang="de"]', 'href', `${BASE}/?lang=de`);
    setMeta('link[rel="alternate"][hreflang="de-DE"]', 'href', `${BASE}/?lang=de`);
    setMeta('link[rel="alternate"][hreflang="it"]', 'href', `${BASE}/?lang=it`);
    setMeta('link[rel="alternate"][hreflang="nl"]', 'href', `${BASE}/?lang=nl`);
    setMeta('link[rel="alternate"][hreflang="x-default"]', 'href', `${BASE}/`);

    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:url"]', 'content', `${BASE}/`);
    setMeta('meta[property="og:title"]', 'content', isPt
      ? 'Aluguer de Material Ortopédico em Lisboa | Gestos Ágeis'
      : 'Gestos Ágeis | Orthopedic Equipment Sales & Rental in Portugal');
    setMeta('meta[property="og:description"]', 'content', isPt
      ? 'Aluguer e venda de equipamento ortopédico em Lisboa: cadeiras de rodas, camas articuladas, andarilhos, canadianas e scooters. Entrega ao domicílio em 24h. Apoio via WhatsApp.'
      : 'Wheelchairs, hospital beds, walkers, mobility scooters and daily living aids. 24h home delivery across Portugal. Store in Benfica, Lisbon. VAT: PT515948039.');
    setMeta('meta[property="og:image"]', 'content', ogImg);
    setMeta('meta[property="og:locale"]', 'content', isPt ? 'pt_PT' : 'en_US');
    setMeta('meta[property="og:site_name"]', 'content', 'Gestos Ágeis Ortopedia');

    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', isPt
      ? 'Aluguer de Material Ortopédico em Lisboa | Gestos Ágeis'
      : 'Gestos Ágeis | Orthopedic Equipment Sales & Rental in Portugal');
    setMeta('meta[name="twitter:description"]', 'content', isPt
      ? 'Aluguer e venda de equipamento ortopédico em Lisboa: cadeiras de rodas, camas articuladas, andarilhos, canadianas e scooters. Entrega ao domicílio em 24h. Apoio via WhatsApp.'
      : 'Rent and buy wheelchairs, hospital beds, walkers and mobility scooters. 24h delivery across Portugal.');
    setMeta('meta[name="twitter:image"]', 'content', ogImg);

    document.documentElement.lang = language || 'pt';

    // JSON-LD: MedicalBusiness / LocalBusiness
    setJsonLd('ga-ld-business', {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${BASE}/#organization`,
          name: 'Gestos Ágeis Unipessoal Lda',
          alternateName: ['Gestos Ágeis', 'Gestos Ágeis Ortopedia'],
          url: BASE,
          logo: { '@type': 'ImageObject', url: LOGO },
          description: isPt
            ? 'Empresa portuguesa especializada em venda e aluguer de material ortopédico e ajudas técnicas. Cadeiras de rodas, camas articuladas, andarilhos, scooters de mobilidade. Loja em Lisboa e entrega ao domicílio em todo Portugal.'
            : 'Portuguese company specializing in the sale and rental of orthopedic equipment and daily living aids. Wheelchairs, hospital beds, walkers, mobility scooters. Store in Lisbon and home delivery across Portugal.',
          legalName: 'Gestos Ágeis Unipessoal Lda',
          taxID: '515948039',
          vatID: 'PT515948039',
          telephone: '+351216035281',
          email: 'geral@gestosageis.com',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Estrada de Benfica 739A',
            addressLocality: 'Lisboa',
            postalCode: '1500-089',
            addressCountry: 'PT'
          },
          sameAs: [
            'https://www.instagram.com/gestos.ageis/',
            'https://www.facebook.com/p/Gestos-%C3%81geis-Ortopedia-100083183657738/',
            'https://gestosageis.com'
          ]
        },
        {
          '@type': 'MedicalBusiness',
          '@id': `${BASE}/#business`,
          parentOrganization: { '@id': `${BASE}/#organization` },
          name: 'Gestos Ágeis Ortopedia',
          alternateName: ['Gestos Ágeis', 'Gestos Ágeis Ortopedia - Benfica'],
          description: isPt
            ? 'Venda e aluguer de material ortopédico em Lisboa e todo Portugal. Cadeiras de rodas, camas articuladas, andarilhos, scooters de mobilidade e ajudas técnicas. Entrega ao domicílio 24h e loja em Benfica.'
            : 'Sales and rental of orthopedic equipment in Lisbon and across Portugal. Wheelchairs, hospital beds, walkers, mobility scooters and daily living aids. 24h home delivery and store in Benfica.',
          url: BASE,
          logo: LOGO,
          image: HERO,
          telephone: '+351216035281',
          email: 'geral@gestosageis.com',
          priceRange: '€€',
          currenciesAccepted: 'EUR',
          paymentAccepted: 'Cash, Credit Card, MB Way, Multibanco, Bank Transfer',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Estrada de Benfica 739A',
            addressLocality: 'Lisboa',
            postalCode: '1500-089',
            addressCountry: 'PT'
          },
          geo: { '@type': 'GeoCoordinates', latitude: 38.7436, longitude: -9.1952 },
          openingHoursSpecification: [
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '09:30', closes: '19:00' }
          ],
          areaServed: { '@type': 'Country', name: 'Portugal' },
          additionalProperty: [
            { '@type': 'PropertyValue', name: 'Delivery Hours', value: '24 hours a day, 7 days a week' },
            { '@type': 'PropertyValue', name: 'Delivery Time', value: 'Next day, often same day in Lisbon' },
            { '@type': 'PropertyValue', name: 'Service', value: 'Sales and rental of orthopedic equipment' }
          ],
          sameAs: [
            'https://www.instagram.com/gestos.ageis/',
            'https://www.facebook.com/p/Gestos-%C3%81geis-Ortopedia-100083183657738/'
          ]
        },
        {
          '@type': 'WebSite',
          '@id': `${BASE}/#website`,
          name: 'Gestos Ágeis',
          url: BASE,
          inLanguage: isPt ? 'pt-PT' : 'en',
          publisher: { '@id': `${BASE}/#organization` }
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Início', item: `${BASE}/` },
            { '@type': 'ListItem', position: 2, name: isPt ? 'Venda e Aluguer de Material Ortopédico' : 'Orthopedic Equipment Sales & Rental', item: `${BASE}/gestosageis` }
          ]
        }
      ]
    });

    // JSON-LD: FAQPage
    const faq = isPt ? faqPt : faqEn;
    setJsonLd('ga-ld-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: isPt ? 'pt-PT' : 'en',
      mainEntity: faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a }
      }))
    });

    // JSON-LD: ItemList of rental categories (Products)
    const cats = [
      { name: isPt ? 'Cadeira de Rodas' : 'Wheelchair', desc: isPt ? 'Aluguer de cadeira de rodas manual com entrega ao domicílio em Portugal.' : 'Manual wheelchair rental with home delivery across Portugal.', price: '5.00' },
      { name: isPt ? 'Cama Articulada' : 'Hospital Bed', desc: isPt ? 'Aluguer de cama articulada elétrica hospitalar com entrega e montagem.' : 'Electric articulated hospital bed rental with delivery and assembly.', price: '15.00' },
      { name: isPt ? 'Andarilho' : 'Walker', desc: isPt ? 'Aluguer de andarilho e auxiliares de marcha para recuperação.' : 'Walker and walking aid rental for recovery.', price: '5.00' },
      { name: isPt ? 'Scooter de Mobilidade' : 'Mobility Scooter', desc: isPt ? 'Aluguer de scooter de mobilidade elétrica para turismo e mobilidade reduzida.' : 'Electric mobility scooter rental for tourism and reduced mobility.', price: '25.00' },
      { name: isPt ? 'Cadeira de Banho' : 'Shower Chair', desc: isPt ? 'Aluguer de cadeira de banho e equipamento de higiene adaptado.' : 'Shower chair and adapted hygiene equipment rental.', price: '5.00' },
      { name: isPt ? 'Bengala' : 'Cane', desc: isPt ? 'Venda e aluguer de bengala e auxiliares de marcha.' : 'Sale and rental of canes and walking aids.', price: '3.00' }
    ];
    const shippingDetails = {
      '@type': 'OfferShippingDetails',
      shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'EUR' },
      shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'PT' },
      deliveryTime: { '@type': 'ShippingDeliveryTime', handlingTime: { '@type': 'QuantitativeValue', minValue: '0', maxValue: '1', unitCode: 'DAY' }, transitTime: { '@type': 'QuantitativeValue', minValue: '1', maxValue: '2', unitCode: 'DAY' } }
    };
    const hasMerchantReturnPolicy = {
      '@type': 'MerchantReturnPolicy',
      applicableCountry: 'PT',
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 14,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/FreeReturn'
    };
    setJsonLd('ga-ld-products', {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: isPt ? 'Material Ortopédico para Venda e Aluguer – Gestos Ágeis' : 'Orthopedic Equipment for Sale & Rental – Gestos Ágeis',
      itemListElement: cats.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Product',
          name: c.name,
          description: c.desc,
          image: LOGO,
          brand: { '@type': 'Brand', name: 'Gestos Ágeis' },
          offers: {
            '@type': 'Offer',
            url: `${BASE}/gestosageis`,
            priceCurrency: 'EUR',
            price: c.price,
            validFrom: new Date().toISOString().split('T')[0],
            priceValidUntil: new Date(new Date().getFullYear() + 1, 11, 31).toISOString().split('T')[0],
            availability: 'https://schema.org/InStock',
            itemCondition: 'https://schema.org/NewCondition',
            seller: { '@type': 'Organization', name: 'Gestos Ágeis Ortopedia' },
            shippingDetails,
            hasMerchantReturnPolicy
          },
          aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '87', bestRating: '5', worstRating: '1' }
        }
      }))
    });

    return () => {
      ['ga-ld-business', 'ga-ld-faq', 'ga-ld-products'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [language, ogImg]);

  return null;
}